import React from 'react'
import { ILoadableDataState } from '../../hooks/use-loadable-data-ref-state'
import { OrgNotificationModel } from '../../models/notification/org-notification.model'
import { AccountDto } from '../../dtos/account.dto'
import { NotificationSummaryCard, Well } from '@pushly/aqe/lib/components'

import { ReloadOutlined } from '@ant-design/icons'
import { StatusType } from '../../enums/status-type'
import { arrayContains, backfillStats, titleCase } from '../../_utils/utils'
import moment from 'moment-timezone'
import { DEFAULT_HIGHCHARTS_CONFIG, HIGHCHARTS_CHANNEL_COLOR_MAP } from '../../constants'
import { HighchartsUtils } from '../../_utils/highcharts-utils'
import HighchartsReact from 'highcharts-react-official'
import Highcharts from 'highcharts'
import { arrayUnqiue } from '../../_utils/array'
import { InsightsReportNonScopedOptions, InsightsReportScopedOptions, InsightsService } from '../../services/insights'
import { Container } from 'typescript-ioc/es5'
import { NotificationDeliveryWindow } from '../../enums/notification-delivery-window'
import { AppState } from '../../stores/app'
import { extractSummaryNotification } from '../../_utils/notifications'
import { numberWithCommas } from '../../_utils/numeral'
import { reduceStatTotalsByChannel } from '../notifications/notification-details/helpers'
import { DeliveryChannel } from '@pushly/aqe/lib/enums/delivery-channels'
import { DeliveryChannelSelector } from '@pushly/aqe/lib/components/delivery-channel-selector/delivery-channel-selector'
import { NotificationPreviewState } from '../../stores/notification-preview-state'
import { InsightsReportContract } from '../reporting_v2/helpers/build-api-report-contract'
import { ReportEntity } from '../reporting_v2/enums/report-entity'
import { ReportDatePreset } from '../reporting_v2/enums/report-date-preset'
import { ReportDateIncrement } from '../reporting_v2/enums/report-date-increment'
import { Button, Col, Divider, Flex, Popover, Row, Select, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { StatsWell, StatItem } from '../../components/details-view/stats-well/'
import { StatusName } from '@pushly/aquarium-utils/lib/enums'

type ChartType = 'impressions' | 'clicks'

interface IOrgNotificationSummaryTabProps {
    notifLoader: React.MutableRefObject<ILoadableDataState<OrgNotificationModel | undefined>>
    orgLoader: React.MutableRefObject<ILoadableDataState<AccountDto | undefined>>
}

interface IState {
    notif?: OrgNotificationModel
    breakdownStats?: any
    loadingBreakdownStats?: boolean
    performanceChannel: DeliveryChannel[]
    statsType: ReportDateIncrement.DAY | ReportDateIncrement.LIFETIME
    previewStateLoaded: boolean
}

class OrgNotificationSummaryTab extends React.Component<IOrgNotificationSummaryTabProps, IState> {
    protected static tabName = 'summary'
    protected static tabLabel = 'Summary'

    protected readonly appState: AppState
    protected readonly notificationPreviewState: NotificationPreviewState
    private readonly insightsService: InsightsService

    public constructor(props: IOrgNotificationSummaryTabProps) {
        super(props)

        this.appState = Container.get(AppState)
        this.insightsService = Container.get(InsightsService)
        this.notificationPreviewState = Container.get(NotificationPreviewState)

        this.state = {
            previewStateLoaded: false,
            performanceChannel: [],
            statsType: ReportDateIncrement.LIFETIME,
        }
    }

    public componentDidMount() {
        this.notificationPreviewState.isReady.then(() => {
            this.setState({ previewStateLoaded: true })
        })
    }

    public componentDidUpdate(_prevProps, prevState, _) {
        const { notif } = this.state
        const { notifLoader } = this.props
        const channelOptions: any[] = notifLoader.current.data?.getVariant()?.getChannels() ?? []

        if (!notif && !!notifLoader.current.data) {
            this.setState({ notif: notifLoader.current.data!, performanceChannel: channelOptions }, () => {
                this.fetchBreakdownStats()
            })
        }

        if (prevState.statsType !== this.state.statsType) {
            this.fetchBreakdownStats()
        }
    }

    public render() {
        const { notifLoader, orgLoader } = this.props
        const org = orgLoader.current.data
        const notif = notifLoader.current.data

        if (!org || !notif) {
            return null
        }

        const npsCache = this.notificationPreviewState.getCache('org', org.id)
        const initialPlatform = npsCache.resolvePlatformFromAvailableChannels(notif.getVariant()?.getChannels())
        const previewState = npsCache.state

        return !this.state.previewStateLoaded ? null : (
            <>
                <div className="outer-well-buttons">
                    <Tooltip title="Refresh">
                        <Button
                            className="refresh-list"
                            type="text"
                            shape="round"
                            icon={
                                <ReloadOutlined className="text--size--large" spin={this.state.loadingBreakdownStats} />
                            }
                            onClick={() => this.fetchBreakdownStats()}
                        />
                    </Tooltip>
                </div>

                {this.renderGenericStats()}
                <br />

                <Row gutter={24} className="details-lower-row">
                    <Col span={14}>{this.renderBreakdownStats()}</Col>
                    <Col span={10}>
                        <NotificationSummaryCard
                            mode="light"
                            loading={notifLoader.current.loading}
                            notification={extractSummaryNotification(
                                notif ?? new OrgNotificationModel(),
                                initialPlatform,
                            )}
                            initialPlatform={initialPlatform}
                            previewState={previewState}
                            hideStats={true}
                            domain={this.appState.currentDomain!}
                            hideFooter={true}
                            userId={this.appState.currentUser?.id}
                        />
                    </Col>
                </Row>
            </>
        )
    }

    protected renderGenericStats() {
        const { notifLoader } = this.props
        const loading = notifLoader.current.loading
        const notif = notifLoader.current.data
        const stats = notif?.getStats()

        const items: StatItem[] = [
            {
                key: 'status',
                type: 'status',
                label: 'Status',
                neverDisabled: true,
                statusName: this.getStatusName(),
            },
            {
                key: 'impressions',
                type: 'numeric',
                label: 'Impressions',
                value: stats?.impressions ?? 0,
                animated: true,
            },
            {
                key: 'clicks',
                type: 'numeric',
                label: 'Clicks (CTR)',
                value: stats?.clicks ?? 0,
                subValue: stats?.ctr_decimal ?? undefined,
                subValueFormat: 'percent',
                animated: true,
            },
        ]

        return <StatsWell loading={loading} disabled={this.isDisabled} items={items} variant="cards" />
    }

    protected getStatusName(): StatusName {
        const { notifLoader } = this.props
        const notif = notifLoader.current.data
        const notifStatus = notif?.getComputedStatus() ?? 'SCHEDULED'
        const completedStatuses = [StatusType.COMPLETED.name, StatusType.COMPLETED_WITH_FAILURES.name]

        let statusText = arrayContains(completedStatuses, notifStatus) ? 'DELIVERED' : notifStatus

        // Map to StatusName enum values
        switch (statusText) {
            case 'DELIVERED':
            case 'COMPLETED':
                return StatusName.COMPLETED
            case 'SCHEDULED':
            case 'SCHEDULING':
                return StatusName.SCHEDULED
            case 'QUEUED':
                return StatusName.QUEUED
            case 'DELIVERING':
                return StatusName.DELIVERING
            case 'FAILED':
                return StatusName.FAILED
            case 'CANCELLED':
            case 'CANCELLING':
                return StatusName.CANCELLED
            case 'DRAFT':
                return StatusName.DRAFT
            default:
                return StatusName.SCHEDULED
        }
    }

    protected renderBreakdownStats() {
        const { notifLoader } = this.props
        const { loadingBreakdownStats } = this.state
        const channelOptions = this.state.notif?.getVariant()?.getChannels() ?? []
        const channelsSelection: any = {}
        channelOptions.forEach((ch) => {
            channelsSelection[ch] = this.state.performanceChannel.includes(ch)
        })

        return (
            <Well
                className="thin primary-stats-well"
                hideHeader={true}
                footerMode="hidden"
                loading={notifLoader.current.loading || loadingBreakdownStats}
                disabled={this.isDisabled}
            >
                <Flex justify="space-between" className="chart-header">
                    <div className="chart-title">Performance Breakdown by Channel</div>
                    <div className="chart-actions">
                        <div>
                            <Select
                                className="stats-type-select"
                                size="small"
                                value={this.state.statsType}
                                onChange={(val) => {
                                    this.setState({ statsType: val })
                                }}
                            >
                                <Select.Option value={ReportDateIncrement.DAY}>Daily</Select.Option>
                                <Select.Option value={ReportDateIncrement.LIFETIME}>All Time</Select.Option>
                            </Select>
                        </div>
                        <div className="channel-selectors">
                            <DeliveryChannelSelector
                                type="multiple"
                                nested={true}
                                onChange={(change: DeliveryChannel[]) => {
                                    this.setState({ performanceChannel: change })
                                }}
                                visibleChannels={channelOptions}
                                disabledChannels={DeliveryChannel.getAllChannels().filter(
                                    (ch) => !channelOptions.includes(ch),
                                )}
                            />
                        </div>
                    </div>
                </Flex>

                {this.renderBreakdownStatsChart()}
            </Well>
        )
    }

    protected renderBreakdownStatsChart() {
        const { breakdownStats, notif } = this.state
        if (!breakdownStats) {
            return ''
        }

        const xAxisDateDataSet = new Set(
            breakdownStats.map((stat) => moment.tz(stat.event_date, 'UTC').format('MMM D')),
        )
        const xAxisChannelDataSet = new Set(notif?.getVariant()?.getChannels().map(DeliveryChannel.getShortName))

        const mapDataKeyFromObject = (key: string, obj: any, channel: DeliveryChannel) => {
            return obj.filter((val) => val.channel === channel).map((filtered) => filtered[key])
        }

        const buildSeries = (chartType: ChartType) => {
            const series: any[] = [{ keys: ['name', 'y'], visible: false, showInLegend: false }]
            const colorType = chartType === 'impressions' ? 'primary' : 'secondary'

            if (this.state.statsType !== ReportDateIncrement.DAY) {
                series.push({
                    data: this.state.performanceChannel.map((channel) => {
                        const foundStat = breakdownStats.find((stat) => stat.channel === channel)
                        return {
                            name: DeliveryChannel.getShortName(channel),
                            color: HIGHCHARTS_CHANNEL_COLOR_MAP[channel][colorType],
                            y: foundStat?.[chartType] ?? 0,
                        }
                    }),
                })
            } else {
                series.push(
                    ...this.state.performanceChannel.map((channel) => ({
                        name: DeliveryChannel.getShortName(channel),
                        color: HIGHCHARTS_CHANNEL_COLOR_MAP[channel][colorType],
                        data: mapDataKeyFromObject(chartType, breakdownStats, channel),
                    })),
                )
            }

            return series
        }

        const options = (chartType: ChartType) => ({
            ...DEFAULT_HIGHCHARTS_CONFIG,
            swContext: (): IState => this.state,
            chart: {
                type: 'column',
                marginLeft: 70,
                marginRight: 50,
                spacingTop: 20,
            },
            title: {
                text: titleCase(chartType),
            },
            xAxis: {
                categories:
                    this.state.statsType === ReportDateIncrement.DAY ? [...xAxisDateDataSet] : [...xAxisChannelDataSet],
            },
            legend: {
                enabled: false,
            },
            yAxis: {
                title: { text: '' },
                allowDecimals: false,
                startOnTick: 0,
                minRange: 0,
            },
            tooltip: {
                pointFormatter: HighchartsUtils.commaTooltipPointFormatter,
            },
            series: buildSeries(chartType),
        })

        const totalStatsByChannel = reduceStatTotalsByChannel(breakdownStats)
        let impressionsTotals = [
            <span key="impressions" className="ant-popover-title">
                Impressions:
            </span>,
        ]
        let clicksTotals = [
            <span key="clicks" className="ant-popover-title">
                Clicks:
            </span>,
        ]

        Object.keys(totalStatsByChannel).map((key) => {
            impressionsTotals.push(
                <div key={`${key}-impressions`} className="reach-breakdown-row">
                    <span className="reach-breakdown-label">{DeliveryChannel.getShortName(DeliveryChannel[key])}:</span>
                    <span className="reach-breakdown-value">
                        {numberWithCommas(totalStatsByChannel[key].impressions)}
                    </span>
                </div>,
            )

            clicksTotals.push(
                <div key={`${key}-clicks`} className="reach-breakdown-row">
                    <span className="reach-breakdown-label">{DeliveryChannel.getShortName(DeliveryChannel[key])}:</span>
                    <span className="reach-breakdown-value">{numberWithCommas(totalStatsByChannel[key].clicks)}</span>
                </div>,
            )
        })

        return (
            <Popover
                key={`popover-details`}
                overlayClassName="org-notif-channel-select-details-popover"
                content={
                    <div>
                        <div className="impressions-breakdown-stats">{impressionsTotals}</div>

                        <div className="clicks-breakdown-stats">{clicksTotals}</div>
                    </div>
                }
                placement="left"
            >
                <HighchartsReact highcharts={Highcharts} options={options('impressions')} />

                <Divider type="horizontal" />

                <HighchartsReact highcharts={Highcharts} options={options('clicks')} />
            </Popover>
        )
    }

    protected get isDelivered(): boolean {
        const { notif } = this.state
        const completedStatuses = [StatusType.COMPLETED.name, StatusType.COMPLETED_WITH_FAILURES.name]
        return (
            !!notif &&
            (arrayContains(completedStatuses, notif.getComputedStatus() ?? StatusType.SCHEDULED.name) ||
                this.isPartialFailure)
        )
    }

    protected get isPartialFailure(): boolean {
        const { notif } = this.state
        const stats = notif?.getStats()
        const hasStats = !!stats && stats.impressions > 0
        return !!notif && notif.getComputedStatus() === StatusType.FAILED.name && hasStats
    }

    protected get isDisabled(): boolean {
        const { notif } = this.state
        const status = notif?.getComputedStatus() ?? StatusType.SCHEDULED.name
        const delivery = notif?.getVariant()?.getDelivery()

        const isDelivered = this.isDelivered
        const isDelivering = status === StatusType.DELIVERING.name
        const isCancelled = status === StatusType.CANCELLED.name || status === StatusType.CANCELLING.name
        const isInf = delivery?.getWindow() === NotificationDeliveryWindow.INFORMED
        const isStz = delivery?.getWindow() === NotificationDeliveryWindow.TIMEZONE

        return !isDelivered && !((isInf || isStz) && (isDelivering || isCancelled))
    }

    protected async fetchBreakdownStats() {
        const { notifLoader, orgLoader } = this.props
        const notif = notifLoader.current.data

        if (!notif) {
            return
        }
        this.setState({ loadingBreakdownStats: true })

        const domainIds = arrayUnqiue(notif.getNotifications().map((n) => n.domainId))
        const channels = notif.getVariant()?.getChannels()

        const insightsPkg: InsightsReportContract = {
            date_preset: ReportDatePreset.LIFETIME,
            date_increment: this.state.statsType,
            action_attribution: 'event_time',
            fields: ['notification_group.id', 'impressions', 'clicks', 'ctr', 'ctr_decimal', 'channel'],
            entity: ReportEntity.NOTIFICATIONS,
            breakdowns: ['notification_group', 'channel'],
            filters: [
                {
                    field: 'domain.id',
                    operator: 'in',
                    value: domainIds,
                },
                {
                    field: 'notification_group.id',
                    operator: 'eq',
                    value: notif.getId(),
                },
                {
                    field: 'channel',
                    operator: 'in',
                    value: channels,
                },
            ],
        }

        const insightsReqOpts: InsightsReportScopedOptions | InsightsReportNonScopedOptions = orgLoader.current?.data
            ?.id
            ? {
                  requestScope: 'organizations',
                  scopeId: orgLoader.current?.data?.id!,
                  nestedScope: 'notifications',
              }
            : { requestScope: null }

        const breakdownStats = await this.insightsService.fetch(insightsPkg, {
            ...insightsReqOpts,
            cancellationKey: 'org-notif-stats',
        })

        let backfilledStats: any[]
        if (this.state.statsType === ReportDateIncrement.DAY) {
            backfilledStats = backfillStats(
                breakdownStats,
                'day',
                {
                    impressions: 0,
                    clicks: 0,
                    ctr_decimal: 0,
                },
                channels,
                {
                    startDate: dayjs(notif.getDeliverySpec().sendDateUtc).tz(orgLoader.current.data?.timezone),
                    endDate: null,
                },
            )
        } else {
            backfilledStats = breakdownStats
        }

        this.setState({
            loadingBreakdownStats: false,
            breakdownStats: backfilledStats,
        })
    }
}

export default OrgNotificationSummaryTab
